import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Tag, X, Check, Copy, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Live-updating remaining time. Returns null once expired so callers can hide. */
const useTimeRemaining = (endsAt) => {
  const compute = () => {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return null;
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return { d, h, m, s, ms };
  };
  const [tr, setTr] = useState(compute);
  useEffect(() => {
    if (!endsAt) { setTr(null); return; }
    setTr(compute);
    // Tick every second when <1h remains, every minute otherwise (kind on CPU)
    const initial = compute();
    const interval = initial && initial.ms < 3600000 ? 1000 : 60000;
    const id = setInterval(() => setTr(compute()), interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt]);
  return tr;
};

/** Compact countdown pill. Style variant matches the parent banner. */
const CountdownPill = ({ endsAt, tone = 'dark' }) => {
  const tr = useTimeRemaining(endsAt);
  if (!tr) return null;
  const { d, h, m, s } = tr;
  const label = d > 0
    ? `${d}d ${h}h`
    : h > 0
      ? `${h}h ${m}m`
      : `${m}m ${String(s).padStart(2, '0')}s`;
  const cls = tone === 'strip'
    ? 'bg-slate-900 text-yellow-300'
    : 'bg-slate-900 text-white';
  return (
    <span
      className={`inline-flex items-center gap-1 ${cls} px-2 py-0.5 rounded font-mono text-xs font-bold`}
      data-testid="promo-countdown"
      aria-live="polite"
    >
      <Clock className="h-3 w-3" /> Ends in {label}
    </span>
  );
};

/**
 * PromoBanner — renders the currently-active promotion(s) for a given surface.
 *
 * variant:
 *   "pricing"  — large card-style banner (yellow accent, headline + subheadline + CTA copy)
 *   "strip"    — thin dismissible bar for the site-wide header (localStorage-remembered)
 *
 * surface must match a key in the backend SURFACE_KEYS:
 *   pricing_banner | site_header_strip
 */
const PromoBanner = ({ variant = 'pricing', surface = 'pricing_banner', onCodeClick }) => {
  const [promos, setPromos] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/promotions/active`, { params: { surface } })
      .then(r => { if (alive) setPromos(r.data.promotions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [surface]);

  useEffect(() => {
    if (variant === 'strip' && promos[0]) {
      const key = `promo_dismissed_${promos[0].code}`;
      setDismissed(localStorage.getItem(key) === '1');
    }
  }, [variant, promos]);

  const promo = promos[0];  // Show top one; rotation can be added in Phase 3
  if (!promo || dismissed) return null;

  // Subheadline uses the admin-provided line as-is. We intentionally no longer
  // append a machine-generated restriction bullet ("Enhanced/Premium ·
  // Monthly/Annual · Business/Event/Job/Classified listings only") — it looked
  // messy on both mobile and desktop. Cadence-specific detail lives on the
  // Pricing page inline ("applied — 25% off for 3 months / your first year")
  // where the user has already picked Monthly vs Annual.
  const fullSubheadline = promo.banner_subheadline || '';

  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(promo.code);
    toast.success(`Copied ${promo.code}`);
    if (onCodeClick) onCodeClick(promo.code);
  };

  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(`promo_dismissed_${promo.code}`, '1');
    setDismissed(true);
  };

  // Where clicking the banner sends the visitor. Admin can override via
  // banner_cta_url; otherwise defaults to /pricing (where the code applies).
  // External URLs (http/https) open in a new tab; internal paths use SPA nav.
  const ctaUrl = (promo.banner_cta_url && promo.banner_cta_url.trim()) || '/pricing';
  const goToCta = (e) => {
    // If the user clicked an inner button (copy, dismiss), let it handle itself.
    if (e && e.defaultPrevented) return;
    if (/^https?:\/\//i.test(ctaUrl)) {
      window.open(ctaUrl, '_blank', 'noopener');
    } else {
      navigate(ctaUrl);
    }
  };
  const ctaLabel = (promo.banner_cta_text && promo.banner_cta_text.trim())
    ? promo.banner_cta_text.trim()
    : 'Get Started';

  if (variant === 'strip') {
    return (
      <div
        onClick={goToCta}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goToCta(e)}
        className="relative bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-900 text-center text-sm py-2 px-10 cursor-pointer hover:brightness-105 transition"
        data-testid="promo-strip"
      >
        <span className="font-medium">{promo.banner_headline}</span>
        <button
          onClick={copyCode}
          className="ml-3 inline-flex items-center gap-1 font-mono font-bold bg-slate-900 text-yellow-300 px-2 py-0.5 rounded hover:bg-slate-800 transition"
          data-testid="promo-strip-code"
        >
          {promo.code} <Copy className="h-3 w-3" />
        </button>
        {promo.show_countdown && promo.ends_at && (
          <span className="ml-3 inline-flex align-middle">
            <CountdownPill endsAt={promo.ends_at} tone="strip" />
          </span>
        )}
        <span className="ml-3 hidden sm:inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2" data-testid="promo-strip-cta">
          {ctaLabel} <ArrowRight className="h-3 w-3" />
        </span>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10"
          data-testid="promo-strip-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Full pricing-banner variant
  return (
    <div
      className="relative overflow-hidden rounded-xl border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50 p-4 sm:p-5 mb-6"
      data-testid="promo-banner"
    >
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-yellow-300/20 rounded-full pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-yellow-400 flex items-center justify-center">
          <Tag className="h-6 w-6 text-slate-900" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-bold text-slate-900" data-testid="promo-banner-headline">
            {promo.banner_headline}
          </div>
          {fullSubheadline && (
            <div className="text-sm text-slate-700 mt-0.5" data-testid="promo-banner-subheadline">{fullSubheadline}</div>
          )}
          {/* Dual-cadence value chips — always visible so users see the annual
              upsell without needing to toggle. Only renders for percent-off
              codes that repeat monthly (e.g. 25% off for 3 months) — on an
              annual invoice the same coupon discounts the first year. */}
          {promo.percent_off && promo.duration_in_months && promo.duration_in_months > 1 && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="promo-banner-cadence-chips">
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/70 border border-slate-300 text-slate-800 px-2 py-1 rounded-full">
                Monthly: {Math.round(promo.percent_off)}% off for {promo.duration_in_months} months
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-600 text-white px-2 py-1 rounded-full">
                Annual: {Math.round(promo.percent_off)}% off — best value
              </span>
            </div>
          )}
          <div className="text-xs text-slate-600 mt-2">
            {promo.first_time_only && <span className="mr-2">• First-time customers only</span>}
            {promo.minimum_amount_gbp && <span>• £{promo.minimum_amount_gbp}+ orders</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {promo.show_countdown && promo.ends_at && (
            <CountdownPill endsAt={promo.ends_at} tone="dark" />
          )}
          <button
            onClick={copyCode}
            className="group flex items-center gap-2 font-mono font-bold text-slate-900 bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 px-3 py-2 rounded-lg transition"
            data-testid="promo-banner-code"
          >
            <span className="text-base">{promo.code}</span>
            <Copy className="h-4 w-4 opacity-60 group-hover:opacity-100" />
          </button>
          <button
            onClick={goToCta}
            className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-900 text-yellow-300 font-semibold hover:bg-slate-800 transition"
            data-testid="promo-banner-cta"
          >
            {ctaLabel} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * PromoValidator — inline input a user can type a code into on the pricing page.
 * Fires onValid(code) when the code is verified. Used to pre-fill the checkout call.
 */
export const PromoValidator = ({ initialCode, onValid, onCleared, billingPeriod, onSwitchBilling }) => {
  const [code, setCode] = useState(initialCode || '');
  const [validated, setValidated] = useState(null);
  const [loading, setLoading] = useState(false);

  // If parent passed an initial code (e.g. from ?promo= URL param), auto-validate
  useEffect(() => {
    if (initialCode && !validated) {
      apply(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  // Human-friendly summary — cadence aware. On Annual we phrase percent-off
  // discounts as "off your first year" so the customer sees the real benefit
  // of committing (a repeating 3-month Stripe coupon on an annual invoice
  // discounts the single yearly invoice — i.e. the whole first year). On
  // Monthly we show the literal duration ("off for 3 months"). Falls back
  // to safe defaults if the promo doc is missing a summary field.
  const buildSummary = (d) => {
    if (!d) return 'Discount applied';
    const pct = d.percent_off, amt = d.amount_off_gbp, months = d.duration_in_months;
    const amtAnnual = d.amount_off_gbp_annual;
    const isAnnual = billingPeriod === 'annual';
    if (isAnnual) {
      // Welcome/upgrade codes: fixed £ saving (6 months at 50% equivalent)
      if (amtAnnual) return `50% off for 6 months — £${Number(amtAnnual).toFixed(0)} off (annual)`;
      // Admin percent-off codes on annual: just show the % without implying duration
      if (pct) return `${Math.round(pct)}% off — annual plan`;
      if (amt) return `£${Number(amt).toFixed(0)} off — annual plan`;
    }
    // Monthly
    if (pct) {
      if (months && months > 1) return `${Math.round(pct)}% off for ${months} months`;
      return `${Math.round(pct)}% off your first month`;
    }
    if (d.summary && String(d.summary).trim()) return d.summary;
    if (d.name && String(d.name).trim()) return d.name;
    if (amt) return `£${Number(amt).toFixed(0)} off`;
    if (months) return `First ${months} month${months !== 1 ? 's' : ''} free`;
    return 'Discount applied';
  };

  const apply = async (raw) => {
    const c = (raw || code).trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/promotions/validate`, { code: c });
      setValidated(r.data);
      onValid && onValid(r.data.code, r.data);
      toast.success(`${r.data.code} applied — ${buildSummary(r.data)}`);
    } catch (e) {
      setValidated(null);
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setCode(''); setValidated(null); onCleared && onCleared();
  };

  if (validated) {
    const pct = validated.percent_off;
    const months = validated.duration_in_months;
    const amtAnnual = validated.amount_off_gbp_annual;
    // isDualCadence: code behaves differently on monthly vs annual
    const isDualCadence = (pct && months && months > 1) || !!amtAnnual;
    const isMonthly = billingPeriod === 'monthly';
    // Annual switch-upsell label — no "year" language per site policy
    const annualUpsellText = amtAnnual
      ? `50% off for 6 months — £${Number(amtAnnual).toFixed(0)} off (annual)`
      : pct ? `${Math.round(pct)}% off on annual — best value` : null;
    return (
      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="promo-applied">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span><span className="font-mono font-bold">{validated.code}</span> applied — <span className="text-emerald-800">{buildSummary(validated)}</span></span>
          </div>
          <button onClick={clear} className="text-xs text-emerald-700 hover:text-emerald-900 underline flex-shrink-0" data-testid="promo-clear">Remove</button>
        </div>
        {/* Dual-cadence upsell — surfaces the fact that Annual gives a whole
            year at the discount rate vs Monthly's short repeating window. */}
        {isDualCadence && isMonthly && annualUpsellText && (
          <div className="mt-2 flex items-center justify-between gap-2 pt-2 border-t border-emerald-200 text-xs">
            <span className="text-emerald-800">
              💡 Switch to <strong>Annual</strong> for <strong>{annualUpsellText}</strong> — best value
            </span>
            {onSwitchBilling && (
              <button
                type="button"
                onClick={onSwitchBilling}
                className="flex-shrink-0 px-2 py-1 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition"
                data-testid="promo-switch-annual"
              >
                Switch to Annual →
              </button>
            )}
          </div>
        )}
        {isDualCadence && !isMonthly && (
          <div className="mt-2 pt-2 border-t border-emerald-200 text-xs text-emerald-800">
            {amtAnnual
              ? <>You're on <strong>Annual</strong> — 50% off for 6 months applied (£{Number(amtAnnual).toFixed(0)} off) 🎉</>
              : <>You're on <strong>Annual</strong> — {Math.round(pct)}% off applied 🎉</>
            }
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-2" data-testid="promo-validator">
      <input
        type="text"
        placeholder="Have a discount code?"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
        className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#0F2C3E]"
        data-testid="promo-input"
      />
      <button
        onClick={() => apply()}
        disabled={loading || !code.trim()}
        className="px-4 py-2 text-sm font-medium rounded-lg bg-[#0F2C3E] text-white hover:bg-[#0F2C3E]/90 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="promo-apply"
      >
        {loading ? 'Checking…' : 'Apply'}
      </button>
    </div>
  );
};

export default PromoBanner;
